import {
  NavigationGuardProvider,
  useNavigationGuardContext,
} from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { WhatsappTemplateCategory } from "@jugl-web/rest-api";
import { ScreenTransitionWrapper } from "@jugl-web/ui-components/cross-platform/ScreenTransitionWrapper";
import {
  BottomCenteredDrawer,
  BottomCenteredDrawerProps,
} from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { Breadcrumb } from "@jugl-web/ui-components/web/DrawerHeader";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import {
  Screen,
  ScreenTransitionManagerProvider,
  useScreenTransitionManager,
} from "@jugl-web/utils/utils/ScreenTransitionManager";
import { usePrevious } from "@web-src/utils/helper";
import React, { ReactNode, useEffect, useMemo, ReactElement } from "react";
import { useInstagramAccount } from "../../providers/InstagramAccountProvider/useInstagramAccount";
import { useWhatsappAccount } from "../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { AIAgentInsightsScreen } from "./screens/AIAgentInsightsScreen/AIAgentInsightsScreen";
import { ConnectedAccountListScreen } from "./screens/ConnectedAccountListScreen";
import { InstagramAccountScreen } from "./screens/InstagramAccountScreen";
import { TwilioAccountScreen } from "./screens/TwilioAccountScreen";
import { WebChatBotAccountScreen } from "./screens/WebChatBotAccountScreen";
import { WhatsappAccountScreen } from "./screens/WhatsappAccountScreen";
import { WhatsappAccountTab } from "./screens/WhatsappAccountScreen/types";
import { WhatsappTemplateFormScreen } from "./screens/WhatsappTemplateFormScreen";
import { ManageAccountsDrawerScreenToParametersMap } from "./types";
import { LiveChatAppAccountScreen } from "./screens/LiveChatAppAccountScreen";

interface ManageAccountsDrawerProps {
  isOpen: boolean;
  initialScreen?: Screen<ManageAccountsDrawerScreenToParametersMap>;
  onRequestClose: () => void;
}

const InnerManageAccountsDrawer: React.FC<ManageAccountsDrawerProps> = ({
  isOpen,
  initialScreen,
  onRequestClose,
}) => {
  const { instagramAccount } = useInstagramAccount();
  const { whatsappAgent } = useWhatsappAccount();

  const { isCx } = useAppVariant();
  const { t } = useTranslations();

  const { requestNavigation } = useNavigationGuardContext();

  const { screen, renderContent, transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const prevIsOpen = usePrevious(isOpen);

  const header = useMemo<BottomCenteredDrawerProps["header"]>(() => {
    const rootBreadcrumb: Breadcrumb = {
      id: "root",
      title: (
        <div className="flex items-center gap-3">
          <SettingsIcon />
          <BreadcrumbText>
            {isCx
              ? t({
                  id: "client-support-page.inbox-settings",
                  defaultMessage: "Inbox Settings",
                })
              : t({
                  id: "client-support-page.crm-settings",
                  defaultMessage: "CRM Settings",
                })}
          </BreadcrumbText>
        </div>
      ),
      onClick: () =>
        requestNavigation(() => transitionTo({ name: "connectedAccountList" })),
    };

    return renderContent<BottomCenteredDrawerProps["header"]>({
      aiAgentInsights: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb],
      },
      connectedAccountList: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb],
      },
      twilioAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "twilioAccount",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.text-sms-chat",
                  defaultMessage: "Text (SMS) Chat",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      liveChatAppAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "liveChatAppAccount",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.live-chat-via-link",
                  defaultMessage: "Live Chat via Link",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      instagramAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          ...(instagramAccount
            ? [
                {
                  id: instagramAccount.id,
                  title: (
                    <BreadcrumbText>
                      {instagramAccount.username ||
                        instagramAccount.business_id}
                    </BreadcrumbText>
                  ),
                },
              ]
            : []),
        ],
      },
      webChatBotAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "customer-support",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.web-chat",
                  defaultMessage: "Web Chat",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      whatsappAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          ...(whatsappAgent
            ? [
                {
                  id: whatsappAgent.waba_id,
                  title: <BreadcrumbText>{whatsappAgent.name}</BreadcrumbText>,
                },
              ]
            : []),
        ],
      },
      whatsappTemplateForm: ({ templateCategory }) => ({
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          ...(whatsappAgent
            ? [
                {
                  id: whatsappAgent.waba_id,
                  title: <BreadcrumbText>{whatsappAgent.name}</BreadcrumbText>,
                  onClick: () =>
                    requestNavigation(() =>
                      transitionTo({
                        name: "whatsappAccount",
                        tab:
                          templateCategory === WhatsappTemplateCategory.message
                            ? WhatsappAccountTab.messageTemplates
                            : WhatsappAccountTab.taskTemplates,
                      })
                    ),
                },
              ]
            : []),
          ...(templateCategory === WhatsappTemplateCategory.message
            ? [
                {
                  id: "message-template",
                  title: (
                    <BreadcrumbText>
                      {t({
                        id: "client-support-page.message-template",
                        defaultMessage: "Message Template",
                      })}
                    </BreadcrumbText>
                  ),
                },
              ]
            : [
                {
                  id: "message-template",
                  title: (
                    <BreadcrumbText>
                      {t({
                        id: "client-support-page.task-template",
                        defaultMessage: "Task Template",
                      })}
                    </BreadcrumbText>
                  ),
                },
              ]),
        ],
      }),
    });
  }, [
    instagramAccount,
    isCx,
    renderContent,
    requestNavigation,
    t,
    transitionTo,
    whatsappAgent,
  ]);

  const content = useMemo<ReactElement>(
    () =>
      renderContent({
        connectedAccountList: <ConnectedAccountListScreen />,
        aiAgentInsights: <AIAgentInsightsScreen />,
        twilioAccount: <TwilioAccountScreen />,
        liveChatAppAccount: <LiveChatAppAccountScreen />,
        instagramAccount: ({ tab }) => <InstagramAccountScreen tab={tab} />,
        webChatBotAccount: () => <WebChatBotAccountScreen />,
        whatsappAccount: ({ tab }) => <WhatsappAccountScreen tab={tab} />,
        whatsappTemplateForm: ({ templateCategory, templateId }) => (
          <WhatsappTemplateFormScreen
            templateCategory={templateCategory}
            templateId={templateId}
          />
        ),
      }),
    [renderContent]
  );

  useEffect(() => {
    if (initialScreen && isOpen && !prevIsOpen) {
      transitionTo(initialScreen, { force: true });
    }
  }, [initialScreen, isOpen, prevIsOpen, transitionTo]);

  return (
    <BottomCenteredDrawer
      isOpen={isOpen}
      header={header}
      onClose={() => requestNavigation(onRequestClose)}
      className="h-[97vh] w-[97vw]"
    >
      <BottomCenteredDrawer.Content className="flex flex-col overflow-x-hidden">
        <ScreenTransitionWrapper
          screenName={screen.name}
          className="min-h-full w-full"
        >
          {content}
        </ScreenTransitionWrapper>
      </BottomCenteredDrawer.Content>
    </BottomCenteredDrawer>
  );
};

export const ManageAccountsDrawer: React.FC<ManageAccountsDrawerProps> = (
  props
) => (
  <NavigationGuardProvider>
    <ScreenTransitionManagerProvider
      initialScreen={{ name: "connectedAccountList" }}
    >
      <InnerManageAccountsDrawer {...props} />
    </ScreenTransitionManagerProvider>
  </NavigationGuardProvider>
);

const BreadcrumbText = ({ children }: { children: ReactNode }) => (
  <span className="font-secondary text-lg text-[#383838]">{children}</span>
);
